import {
  ShareRole,
  ShareStatus,
  ShareTargetType,
} from 'networking/controllers/shares-controller';

// What each role means where, worded for the people choosing it. Mirrors the
// rules in api/app/services/sharing/access.rb.

type RoleOption = { role: ShareRole; label: string; description: string };

// The limited contributor (adds to Unsorted, sees only their own) isn't
// offered until it's agreed on; existing ones still show and keep working.
export const SHOW_LIMITED_CONTRIBUTOR = false;

const LIMITED_CONTRIBUTOR: RoleOption = {
  role: 'limited_contributor',
  label: 'Limited contributor',
  description: 'Adds memories to Unsorted. Sees only what they added.',
};

export const ROLE_OPTIONS: Record<ShareTargetType, RoleOption[]> = {
  vault: [
    {
      role: 'viewer',
      label: 'Viewer',
      description: "Sees everything that isn't private. Can't change anything.",
    },
    {
      role: 'contributor',
      label: 'Contributor',
      description:
        'Adds memories, albums and prompts, and changes only their own. ' +
        "Doesn't see private memories.",
    },
    ...(SHOW_LIMITED_CONTRIBUTOR ? [LIMITED_CONTRIBUTOR] : []),
    {
      role: 'editor',
      label: 'Editor',
      description:
        'Sees and changes everything, and invites others. No access to billing.',
    },
  ],
  album: [
    {
      role: 'viewer',
      label: 'Viewer',
      description: 'Sees this album, except its private memories.',
    },
    {
      role: 'contributor',
      label: 'Contributor',
      description:
        'Sees this album, except its private memories, and adds to it. ' +
        'Changes only their own.',
    },
    {
      role: 'editor',
      label: 'Editor',
      description:
        'Changes everything in this album, private memories included, and ' +
        'shares it.',
    },
  ],
  memory: [
    { role: 'viewer', label: 'Viewer', description: 'Sees this memory.' },
    {
      role: 'contributor',
      label: 'Collaborator',
      description: 'Sees this memory and can edit its description.',
    },
  ],
};

export const roleLabel = (
  role: ShareRole | null | undefined,
  on: ShareTargetType = 'vault',
): string => {
  if (!role) return '';
  if (role === 'owner') return 'Owner';
  const option = ROLE_OPTIONS[on].find((item) => item.role === role);
  if (option) return option.label;
  if (role === 'limited_contributor') return LIMITED_CONTRIBUTOR.label;
  return ROLE_OPTIONS.vault.find((item) => item.role === role)?.label || role;
};

/**
 * The roles to offer for someone who already has one: the usual ones, plus
 * their current role when it's no longer offered (an existing limited
 * contributor), so the dropdown shows it.
 */
export const roleChoices = (
  current: ShareRole,
  on: ShareTargetType,
): RoleOption[] => {
  const options = ROLE_OPTIONS[on] || ROLE_OPTIONS.vault;
  if (options.some((option) => option.role === current)) return options;
  return current === 'limited_contributor'
    ? [...options, LIMITED_CONTRIBUTOR]
    : options;
};

export const STATUS_LABEL: Record<ShareStatus, string> = {
  invited: 'Invited',
  active: 'Active',
  declined: 'Declined',
  removed: 'Removed',
  expired: 'Expired',
};

export const TARGET_LABEL: Record<ShareTargetType, string> = {
  vault: 'Vault',
  album: 'Album',
  memory: 'Memory',
};
